<template>
  <div class="card mt-4">
    <div class="table-responsive" v-on:scroll.passive="handleScroll">
      <div class="table-controls m-3">
        <div class="controls-wrap d-flex">
          <input
            type="search"
            id="integration-search"
            class="form-control mr-2"
            aria-label="SearchGrid"
            aria-describedby="searchGrid"
            placeholder="Search (name)"
            v-model="searchWord"
            @input="checkSearch"
            @keyup.enter="doSearch(searchWord)"
          />

          <button
            class="btn btn-outline mr-4"
            type="button"
            @click="addFilter((input = {}), filters)"
          >
            <i class="fas fa-plus-circle"></i>
          </button>

          <div class="d-flex">
            <div class="empl ml-auto">
              <v-select
                multiple
                @input="handleSearchFilters"
                v-model="serviceLevelSelectModel"
                placeholder="Agreement"
                inputId="service-level-select"
                :options="[
                  { id: 'sourcing', agreement: 'Sourcing' },
                  { id: 'full_service', agreement: 'Full-service' },
                  { id: 'nearshore', agreement: 'Nearshore' },
                  { id: 'freelances', agreement: 'Freelancers' },
                ]"
                label="agreement"
                :reduce="service => service.id"
                :clearable="false"
              ></v-select>
            </div>
            <router-link class="ml-auto btn btn-success text-nowrap m-mobile" to="/add-employer"
              >Add Employer</router-link
            >
            <button
              type="button"
              class="btn btn-success text-nowrap desktop-only"
              @click="showModal = true"
            >
              Add Employer
              <portal to="add-employer-modal">
                <CustomMainModal @close="showModal = false" v-show="showModal">
                  <template v-slot:header>
                    <h5>Add Employer</h5>
                  </template>
                  <AddEmployerBtnModal v-if="showModal" @new-emp="refreshEmployers" />
                </CustomMainModal>
              </portal>
            </button>
          </div>

          <portal-target name="add-employer-modal"></portal-target>
        </div>

        <!-- FILTERS -->
        <section class="filter-items">
          <div class="row mt-3">
            <div
              v-for="(input, index) in filters"
              :key="`phoneInput-${index}`"
              class="col-md-8 filter"
            >
              <div class="d-flex">
                <select
                  @change="selectedFilterType"
                  name="select-filter"
                  class="form-control filter-select"
                  v-model="selectedFilterName[index]"
                >
                  <option value="location">Location</option>
                  <option value="distance">City (distance)</option>
                  <option value="employment">Employment type</option>
                  <option value="competences">Competencies</option>
                  <option value="relocation">Relocation</option>
                </select>

                <v-select
                  v-if="selectedFilterName[index] == 'location'"
                  @input="handleVSelectFields($event, selectedFilterName)"
                  @search="fetchOptions"
                  v-model="input['location']"
                  class="mr-1 w-100"
                  label="name"
                  placeholder="Location"
                  aria-label="location"
                  aria-describedby="location-input"
                  :options="[...locationsOptions]"
                  :reduce="location => location"
                  :clearable="false"
                  multiple
                >
                  <template v-slot:no-options>
                    <div style="opacity: 0.5">Please type at least 3 characters</div>
                  </template>
                </v-select>

                <v-select
                  v-if="selectedFilterName[index] == 'distance'"
                  @change="handleVSelectFields($event, selectedFilterName)"
                  @search="fetchOptionsForCities"
                  v-model="input['city']"
                  class="mr-1 w-100"
                  inputId="city-distance"
                  label="name"
                  placeholder="City name"
                  aria-label="city-distance"
                  aria-describedby="city-distance-input"
                  :options="citiesByNameOptions"
                  :reduce="city => city"
                  :clearable="false"
                  autocorrect="off"
                  spellcheck="false"
                  autocomplete="off"
                >
                  <template v-slot:no-options>
                    <div style="opacity: 0.5">Please type at least 3 characters</div>
                  </template>
                </v-select>

                <input
                  v-if="selectedFilterName[index] == 'distance'"
                  @input="toggleLabel($event)"
                  v-model="input['distance']"
                  type="text"
                  name="distance"
                  style="max-width: 145px; min-width: 145px"
                  class="form-control mr-1"
                  aria-describedby="CityDistanceHelp"
                  placeholder="Distance (km)"
                />
                <label
                  v-if="selectedFilterName[index] == 'distance' && showLabel"
                  class="custom-label"
                  for="distance"
                  >km</label
                >

                <select
                  v-if="selectedFilterName[index] == 'employment'"
                  @input="handleVSelectFields($event, selectedFilterName)"
                  class="form-control w-100"
                  name="employment"
                  v-model="input['employment']"
                >
                  <option value="1">Employment</option>
                  <option value="2">Self-employment</option>
                  <!--  <option value="3">Employment / Self-Employment</option> -->
                </select>

                <v-select
                  v-if="selectedFilterName[index] == 'competences'"
                  @input="handleVSelectFields($event, selectedFilterName)"
                  v-model="input['competences']"
                  class="w-100 mr-1"
                  placeholder="Competencies"
                  label="name"
                  :options="[...getCompetencesFilterList]"
                  :reduce="competence => competence.id"
                  :clearable="false"
                  multiple
                >
                  <template v-slot:no-options>
                    <div style="opacity: 0.5">Sorry, no matching competence</div>
                  </template>
                </v-select>

                <select
                  v-if="selectedFilterName[index] == 'serviceLevel'"
                  class="form-control mr-0"
                  v-model="input['serviceLevel']"
                  @input="handleVSelectFields($event, selectedFilterName)"
                  name="service-level"
                >
                  <option>Basic</option>
                  <option>Sourcing</option>
                  <option>Full-service</option>
                </select>

                <select
                  v-if="selectedFilterName[index] == 'relocation'"
                  @input="handleVSelectFields($event, selectedFilterName)"
                  class="form-control"
                  v-model="input['relocation']"
                  name="relocation"
                >
                  <option :value="false">No</option>
                  <option :value="true">Yes</option>
                </select>

                <button
                  class="btn btn-outline-remove mr-1"
                  type="button"
                  @click="removeSingleFilter(index, filters)"
                >
                  <i class="fas fa-times-circle"></i>
                </button>
              </div>
            </div>
          </div>

          <div class="row" v-show="filters.length > 0">
            <div class="col-md-8 d-flex filter">
              <div class="ml-auto">
                <button
                  type="button"
                  class="btn btn-success text-nowrap text-white mt-2 mr-2"
                  @click="applyFilters"
                >
                  Apply Filters
                </button>
                <button
                  style="margin-right: 8px;"
                  type="button"
                  class="btn btn-danger text-nowrap text-white mt-2"
                  @click="removeAllFilters"
                >
                  Remove Filters
                </button>
                <button
                  style="position: relative; left: -4px;"
                  class="btn btn-outline mt-2"
                  type="button"
                  @click="addFilter((input = {}), filters)"
                >
                  <i class="fas fa-plus-circle"></i>
                </button>
              </div>
            </div>
          </div>
        </section>

        <!-- END FILTERS -->
      </div>
      <div v-if="isLoading" class="loader-empl"><Spinner :isLoading="isLoading" /></div>

      <table class="table table-striped custom-table">
        <thead class="bg-dark">
          <tr>
            <th style="width: 240px" nowrap scope="col">NAME</th>
            <th nowrap scope="col">CONTACT</th>
            <th nowrap scope="col" style="width: 180px">LOCATION</th>
            <!-- <th nowrap scope="col" style="width: 80px; text-align: center">&nbsp;</th> -->
            <th nowrap scope="col">UPDATED</th>
            <th nowrap cpope="col">JOBS</th>
            <th nowrap cpope="col">HIRES</th>
          </tr>
        </thead>
        <tbody>
          <tr class="list-complete-item" v-for="employer in getEmployersList" :key="employer.id">
            <td id="name" class="popover-show" nowrap>
              <div slot="reference" class="truncate-item">
                <router-link
                  class="d-flex justify-content-start"
                  :to="{
                    name: 'employer_id',
                    params: { employer_id: employer.id, employer_name: employer.name },
                  }"
                  >{{ employer.name }}</router-link
                >
              </div>
            </td>
            <td id="contact">
              <span>{{ employer.first_name }} {{ employer.last_name }}</span>
            </td>
            <td id="location" nowrap>
              <div class="text-truncate">{{ getLocation(employer.city, employer.country) }}</div>
            </td>
            <!--             <td id="agreement-level" nowrap class="p-0">
              <popper
                trigger="hover"
                :options="{
                  placement: 'top',
                  modifiers: { offset: { offset: '0,10px' } },
                }"
              >
                <div class="popper">
                  {{ agreementType(employer.agreement) }}
                </div>
                <div slot="reference">
                  <div class="popover-show">
                    <svgHelper
                      class="ml-4"
                      :name="agreementType(employer.agreement).toLowerCase()"
                    />
                  </div>
                </div>
              </popper>
            </td> -->
            <td id="candidate-updated" class="popover-show" nowrap>
              <popper
                trigger="hover"
                :options="{
                  placement: 'top',
                  modifiers: { offset: { offset: '-60,10px' } },
                }"
              >
                <div class="popper">
                  {{
                    employer.updated_at
                      | formatDate({
                        format: getUserProfile.format_date,
                        timezone: getUserProfile.timezone,
                        hasHourAndMinutes: true,
                      })
                  }}
                </div>
                <div style="padding-left: 16px;" slot="reference">
                  {{
                    employer.updated_at
                      | formatDate({
                        format: getUserProfile.format_date,
                        timezone: getUserProfile.timezone,
                        hasHourAndMinutes: false,
                      })
                  }}
                </div>
              </popper>
            </td>
            <td id="jobs">
              <span>{{ employer.jobs_count }}</span>
            </td>
            <td id="hires">
              <span>{{ employer.hires_count }}</span>
            </td>
          </tr>
        </tbody>
      </table>

      <ErrorList class="no-data-message" v-show="getEmployersList.length == 0" name="noEmployers" />
    </div>

    <div class="d-flex ml-auto mt-auto bottom-controls">
      <div id="pagination">
        <Pagination
          :currentPage="getCurrentPage"
          :totalPages="getTotalPages"
          @nextPage="nextPage"
          @keyup="keymonitor"
          @prevPage="previousPage"
          @selectPageByNumber="setPageBynumber"
          @firstPage="setToFirstPage"
          @lastPage="setToLastPage"
        ></Pagination>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import axios from 'axios';

/* import ServiceLevel from '../../common/ServiceLevel'; */

import Pagination from '@/components/Navigations/Pagination';

import Search from '../../common/Search/Search';

import CustomMainModal from '../../Modals/CustomMainModal';
import AddEmployerBtnModal from './AddEmployerBtnModal';

import { paginationMixin } from '../../../mixins/paginationMixin';
import { displayResultsBasedOnScreenHeightMixin } from '../../../mixins/displayResultsBasedOnScreenHeightMixin';
import { agreementTypeMixin } from '../../../mixins/agreementTypeMixin';
import { serviceLevelMixin } from '../../../mixins/serviceLevelMixin';

import { localStorageHelper } from '@/utils/helpers';
import { errorService } from '../../../services/errorService';
import ErrorList from '@/components/common/errorsList';

import { MESSAGES } from '../../../constants';
import titleMixin from '../../../mixins/titleMixin';

export default {
  components: {
    Pagination,
    CustomMainModal,
    Search,
    AddEmployerBtnModal,
    ErrorList,
  },
  mixins: [
    paginationMixin,
    displayResultsBasedOnScreenHeightMixin,
    agreementTypeMixin,
    serviceLevelMixin,
    titleMixin,
  ],
  title() {
    return 'Employers | Jobshark';
  },
  data: () => ({
    /* Filters */
    locationsOptions: [],
    citiesByNameOptions: [],
    selectedFilterName: [],
    filters: [],
    listOfFilters: [],
    /* End Filters */
    serviceLevelSelectModel: [],
    searchText: '',
    searchWord: '',
    isLoading: true,
    infiniteScrollList: [],
    showModal: false,
    stylesObj: {
      boxShadow: '',
    },
    currentPage: 1,
    pageCount: 10,
    showLabel: false,
  }),
  methods: {
    ...mapActions([
      'retrieveCandidates',
      'retrieveEmployersList',
      'searchLocationByName',
      'retrieveCompetencesList',
    ]),
    /* Filters */
    toggleLabel(e) {
      e.target.value ? (this.showLabel = true) : (this.showLabel = false);
    },

    checkSearch() {
      if (this.searchWord === '') {
        this.doSearch(this.searchWord);
      }
    },

    selectedFilterType(e) {
      if (e.target.value == 'distance') {
        this.filters.forEach(el => {
          if (el && Object.keys(el).length === 0) {
            el['distance'] = 100;
            this.showLabel = true;
          }
        });
      }
      if (e.target.value == 'employment') {
        this.filters.forEach(el => {
          if (el && Object.keys(el).length === 0) {
            el['employment'] = '1';
          }
        });
      }

      if (e.target.value == 'relocation') {
        this.filters.forEach(el => {
          if (el && Object.keys(el).length === 0) {
            el['relocation'] = false;
          }
        });
      }
    },
    handleVSelectFields() {},
    fetchOptions(search, loading) {
      if (search.length < 3) {
        this.locationsOptions = [];
      }

      if (search.length >= 3) {
        loading(true);

        this.searchLocationByName(search)
          .then(response => {
            const citiesList = response.data.cities;
            const countriesList = response.data.countries;

            const concatedLists = [].concat(countriesList, citiesList);

            this.locationsOptions = concatedLists;

            loading(false);
          })
          .catch(err => {
            loading(false);
            throw err;
          });
      }
    },
    fetchOptionsForCities(search, loading) {
      if (search.length > 1) {
        loading(true);
        this.searchLocationByName(search)
          .then(response => {
            this.citiesByNameOptions = response.data.cities;

            loading(false);
          })
          .catch(err => {
            loading(false);
            throw err;
          });
      }
    },
    addFilter(_value, fieldType) {
      fieldType.push({});
      this.selectedFilterName.push('location');
    },
    removeSingleFilter(index, fieldType) {
      fieldType.splice(index, 1);
      this.selectedFilterName.splice(index, 1);

      this.reloadDefaultList();
    },
    removeAllFilters() {
      this.filters = [];
      this.selectedFilterName = [];

      this.reloadDefaultList();
    },
    applyFilters() {
      // Remove invalid entries
      this.selectedFilterName.forEach((filterType, index) => {
        if (filterType == 'distance') {
          Object.keys(this.filters[index] || {}).forEach(key =>
            this.selectedFilterName.includes(key),
          );
        } else {
          Object.keys(this.filters[index] || {}).forEach(
            key => this.selectedFilterName.includes(key) || delete this.filters[index][key],
          );
        }
      });

      this.filters.forEach(item => {
        if (item['location']) {
          let tempList = item.location.map(i => i.id);

          this.listOfFilters.push({ location: tempList });

          //TODO: Send to API
        }

        if (item['city']) {
          this.listOfFilters.push({
            distance: {
              city_id: item['city'].id,
              distance: item['distance'] || 0,
            },
          });
        }

        if (item['employment']) {
          this.listOfFilters.push({
            employment: Number(item.employment),
          });
        }

        if (item['competences']) {
          this.listOfFilters.push(item);
        }

        if (item['relocation'] == true || item['relocation'] == false) {
          this.listOfFilters.push({
            relocation: item.relocation,
          });
        }
      });

      this.doSearchWithFilters(this.listOfFilters);

      //Reset List
      this.listOfFilters = [];
    },
    reloadDefaultList() {
      this.isLoading = true;

      this.selectedFiltersList = [];
      this.currentPage = 1;

      this.retrieveEmployersList({
        search_word: this.searchText,
        page: this.currentPage,
        count: this.pageCount,

        agreement_level: this.serviceLevelSelectModel.map(item => item.id),
        filters: [],
      })
        .then(() => {
          this.isLoading = false;
        })
        .catch(err => {
          this.isLoading = false;

          if (err.response.status == 401) {
            localStorageHelper();

            this.$toasted.error(MESSAGES.expiredSession, {
              theme: 'bubble',
              icon: 'fa-check-circle',
              position: 'bottom-right',
              duration: 5000,
            });

            this.$router.push({ name: 'home' });
            location.reload();
          }

          this.$toasted.error(errorService(err), {
            theme: 'bubble',
            icon: 'fa-times-circle',
            position: 'bottom-right',
            duration: 8000,
          });
        });
    },
    /* End Filters */
    doSearch(searchedText) {
      this.currentPage = 1;

      if (searchedText.length >= 2) {
        this.isLoading = true;
        this.searchText = searchedText;

        this.retrieveEmployersList({
          search_word: searchedText,
          page: this.currentPage,
          count: this.pageCount,
          agreement_level: this.serviceLevelSelectModel.map(item => item.id),
        })
          .then(() => {
            this.isLoading = false;
          })
          .catch(err => {
            this.isLoading = false;

            this.$toasted.error(errorService(err), {
              theme: 'bubble',
              icon: 'fa-times-circle',
              position: 'bottom-right',
              duration: 8000,
            });
          });
      }

      if (searchedText === '') {
        this.isLoading = true;
        this.searchText = searchedText;

        this.retrieveEmployersList({
          search_word: '',
          page: this.currentPage,
          count: this.pageCount,
          agreement_level: this.serviceLevelSelectModel.map(item => item.id),
        })
          .then(() => {
            this.isLoading = false;
          })
          .catch(err => {
            this.isLoading = false;

            this.$toasted.error(errorService(err), {
              theme: 'bubble',
              icon: 'fa-times-circle',
              position: 'bottom-right',
              duration: 8000,
            });
          });
      }
    },
    doSearchWithFilters(filters) {
      this.isLoading = true;
      this.currentPage = 1;
      this.selectedFiltersList = filters;

      this.retrieveEmployersList({
        search_word: this.searchText,
        page: this.currentPage,
        count: this.pageCount,
        agreement_level: this.serviceLevelSelectModel.map(item => item.id),
        filters: filters,
      })
        .then(() => {
          this.isLoading = false;
        })
        .catch(err => {
          this.isLoading = false;
          throw err;
        });
    },
    handleSearchFilters() {
      this.isLoading = true;

      this.retrieveEmployersList({
        page: 1,
        count: this.pageCount,
        agreement_level: this.serviceLevelSelectModel,
        search_word: this.searchText,
        filters: this.selectedFiltersList,
      })
        .then(() => {
          this.isLoading = false;
        })
        .catch(err => {
          this.isLoading = false;

          this.$toasted.error(errorService(err), {
            theme: 'bubble',
            icon: 'fa-times-circle',
            position: 'bottom-right',
            duration: 8000,
          });
        });
    },
    getLocation(city, country) {
      if (city && country) {
        return `${city.name}, ${country.name}`;
      }
    },
    infiniteHandler($state) {
      axios
        .get(`http://test4shark.jobshark.se/api/employersList`, {
          params: {
            page: this.currentPage,
            count: 10,
          },
          headers: {
            Authorization: `Bearer ${this.getToken}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        })
        .then(({ data }) => {
          const list = data.data.result.data;

          if (list.length) {
            this.currentPage += 1;
            this.infiniteScrollList.push(...list);
            $state.loaded();
          } else {
            $state.complete();
          }
        })
        .catch(err => {
          this.isLoading = false;

          this.$toasted.error(errorService(err), {
            theme: 'bubble',
            icon: 'fa-times-circle',
            position: 'bottom-right',
            duration: 8000,
          });
        });
    },
    handleScroll(e) {
      if (e.target.scrollLeft > 0) {
        this.stylesObj.boxShadow = 'rgba(0,0,0,0.75) 7px 8px 17px -14px';
      } else {
        this.stylesObj.boxShadow = '';
      }
    },
    keymonitor(event) {
      // who caused it? "event.target.id"

      // what was pressed?
      let keyMessage = event.key || String.fromCharCode(event.keyCode);

      /* if (event.shiftKey) {
        keyMessage += 'Shift+';
      } */

      if (keyMessage == 'ArrowRight' && !this.showModal) {
        this.nextPage();
      }

      if (keyMessage == 'ArrowLeft' && !this.showModal) {
        this.previousPage();
      }
    },
    /* PAGINATION */
    nextPage() {
      if (this.currentPage >= 1) {
        this.isLoading = true;

        this.currentPage++;
        this.retrieveEmployersList({ page: this.currentPage, count: this.pageCount })
          .then(() => {
            this.isLoading = false;
          })
          .catch(err => {
            this.isLoading = false;
            throw err;
          });
      }
    },
    previousPage() {
      this.isLoading = true;
      if (this.currentPage > 1) {
        --this.currentPage;
        this.retrieveEmployersList({ page: this.currentPage, count: this.pageCount })
          .then(() => {
            this.isLoading = false;
          })
          .catch(err => {
            this.isLoading = false;
            throw err;
          });
      } else {
        this.currentPage = 1;
      }
    },
    setPageBynumber(pageNumber) {
      if (pageNumber > this.getTotalPages || pageNumber < 1) return;
      this.isLoading = true;

      this.currentPage = pageNumber;
      this.retrieveEmployersList({ page: this.currentPage, count: this.pageCount })
        .then(() => {
          this.isLoading = false;
        })
        .catch(err => {
          this.isLoading = false;
          throw err;
        });
    },
    setToFirstPage() {
      this.currentPage = 1;
      this.isLoading = true;

      this.retrieveEmployersList({ page: this.currentPage, count: this.pageCount })
        .then(() => {
          this.isLoading = false;
        })
        .catch(err => {
          this.isLoading = false;
          throw err;
        });
    },
    setToLastPage(lastPage) {
      this.isLoading = true;

      this.currentPage = lastPage;
      this.retrieveEmployersList({ page: this.currentPage, count: this.pageCount })
        .then(() => {
          this.isLoading = false;
        })
        .catch(err => {
          this.isLoading = false;
          throw err;
        });
    },

    refreshEmployers() {
      this.isLoading = true;
      this.retrieveEmployersList({
        search_word: '',
        page: 1,
        count: this.pageCount,
        agreement_level: [],
      }).then(() => {
        this.isLoading = false;
        this.showModal = false;
      });
    },
  },
  computed: {
    ...mapGetters([
      'getEmployersList',
      'getUserProfile',
      'getCurrentPage',
      'getTotalPages',
      'getToken',
      'getCompetencesFilterList',
    ]),
  },
  mounted() {
    /*     this.isLoading = false; */
    this.currentPage = 1;

    this.displayResultsBasedOnScreen();

    this.retrieveCompetencesList();

    this.retrieveEmployersList({
      search_word: this.searchText,
      page: this.currentPage,
      count: this.pageCount,
      agreement_level: this.serviceLevelSelectModel.map(item => item.id),
    })
      .then(() => {
        this.isLoading = false;
      })
      .catch(err => {
        this.isLoading = false;
        if (err.response.status == 401) {
          localStorageHelper();

          this.$toasted.error(MESSAGES.expiredSession, {
            theme: 'bubble',
            icon: 'fa-check-circle',
            position: 'bottom-right',
            duration: 5000,
          });

          this.$router.push('/');
        }
      });
  },
  beforeDestroy() {
    this.isLoading = false;
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_variables.scss';

.loader-empl {
  position: relative;
  width: 100%;
  height: 100%;
}
.custom-label {
  line-height: 40px;
  position: absolute;
  right: 70px;
  top: 0;
  bottom: 0;
  -moz-transition: 0.3s right ease;
  -ms-transition: 0.3s right ease;
  -o-transition: 0.3s right ease;
  -webkit-transition: 0.3s right ease;
  transition: 0.3s right ease;
  z-index: 0;
}

select {
  min-width: 120px;
  box-shadow: 0px 2px 3px #0000000d;
  border: 1px solid #d7dae2;
  border-radius: 4px;
}
.card {
  height: 81.7vh;
}
.mobile-only {
  display: none;
}

.desktop-only-table {
  display: table;
}
.m-mobile {
  display: none;
}
.custom-table {
  min-width: 100%;
  tr {
    td {
      &:first-of-type {
        padding-left: 21px;
        a {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          display: block;
        }
      }
      &:nth-of-type(2) {
        span {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          display: block;
        }
      }
    }
  }
  .popover-show {
    padding: 7px 0 0px 0;
  }
  .text-wrap {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

@media only screen and (max-width: 1251px) {
  .card {
    margin-left: 23px !important;
    margin-right: 23px !important;
  }
}

@media only screen and (max-width: 992px) {
  .card {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  .desktop-only-table,
  .desktop-only {
    display: none;
  }
  .mobile-only {
    display: block;
  }
  .m-mobile {
    display: inline-block;
  }
}

@media only screen and (max-width: 768px) {
  .desktop-only-table,
  .desktop-only {
    display: none;
  }
  .mobile-only {
    display: block;
  }
}

@media only screen and (max-width: 576px) {
  .desktop-only-table,
  .desktop-only {
    display: none;
  }
  .mobile-only {
    display: block;
  }
  .custom-table {
    display: block;
  }
}

.vs--searchable .vs__dropdown-toggle {
  height: calc(1.5em + 0.75rem + 2px);
}

.col-md-8.filter {
  color: #6c757d;
  margin-bottom: 12px;
  font-family: $font-family-sans-lato;
}

.filter-select {
  min-width: 200px;
  max-width: 200px;
  margin-right: 6px !important;
  padding-bottom: 8px;
}

@media only screen and(max-width: 576px) {
  .table-controls {
    .controls-wrap {
      display: block !important;
      .form-control {
        width: 100%;
        margin-bottom: 12px;
      }
      .btn-outline,
      .btn-success {
        display: none;
      }
      .empl {
        margin-right: 0 !important;
        width: 100%;
      }
    }
  }
}

@media only screen and(min-width: 576px) and (max-width: 768px) {
  .table-controls {
    .controls-wrap {
      .form-control {
        width: 50%;
        margin-bottom: 12px;
      }
      .btn-outline,
      .btn-success {
        display: none;
      }
      .empl {
        margin-right: 0 !important;
        width: 100%;
      }
    }
  }
}
@media only screen and(min-width: 768px) and (max-width: 992px) {
}
@media only screen and(min-width: 992px) and (max-width: 1150px) {
}
</style>
